<template>
  <div class="pointer-events-none fixed inset-0 z-10">
    <button
      class="pointer-events-auto absolute bottom-0 right-0 z-10 mb-8 mr-4 flex h-16 w-16 items-center justify-center rounded-full bg-blue-darker bg-opacity-75 p-0 text-white"
      :aria-label="label"
      @click="isOpen = !isOpen"
    >
      <template v-if="isOpen">
        <svg class="h-8 w-8" viewBox="0 0 20 20" fill="currentColor">
          <path
            fill-rule="evenodd"
            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
            clip-rule="evenodd"
          ></path>
        </svg>
      </template>
      <template v-else>
        <svg class="h-8 w-8" viewBox="0 0 20 20" fill="currentColor">
          <path
            fill-rule="evenodd"
            d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
            clip-rule="evenodd"
          ></path>
        </svg>
      </template>
    </button>

    <aside
      v-if="isOpen"
      class="pointer-events-auto z-10 h-full w-full overflow-y-auto overscroll-none bg-slate-lighter py-8"
    >
      <div class="container space-y-4 text-center">
        <search-input></search-input>
        <nav class="mt-navigation text-white" role="navigation">
          <ul class="-m-1 flex flex-wrap">
            <li class="w-1/2 p-1">
              <navigation-item
                active-class="bg-purple-lighter text-white"
                exact
                class="min-h-navigation-item"
                label="Framsida"
                to="/"
                :icon="IconHome"
              ></navigation-item>
            </li>
            <li class="w-1/2 p-1">
              <navigation-item
                active-class="bg-peach-base text-white"
                class="min-h-navigation-item"
                label="Djurfakta"
                to="/djurfakta"
                :icon="IconWhale"
              ></navigation-item>
            </li>
            <li class="w-1/2 p-1">
              <navigation-item
                active-class="bg-green-base text-white"
                class="min-h-navigation-item"
                label="Hjälp djuren"
                to="/hjalp-djuren"
                :icon="IconChick"
              ></navigation-item>
            </li>
            <li class="w-1/2 p-1">
              <navigation-item
                active-class="bg-blue-base text-white"
                class="min-h-navigation-item"
                label="Klubbnytt"
                to="/klubbnytt"
                :icon="IconMagazine"
              ></navigation-item>
            </li>
            <li class="w-1/2 p-1">
              <navigation-item
                active-class="bg-green-dark text-white"
                class="min-h-navigation-item"
                label="App"
                to="/app"
                :icon="IconMobilePhone"
              ></navigation-item>
            </li>
            <li class="w-1/2 p-1">
              <navigation-item
                active-class="bg-pink-base text-white"
                class="min-h-navigation-item"
                label="Om oss"
                to="/om-oss"
                :icon="IconAboutUs"
              ></navigation-item>
            </li>
            <li class="w-1/2 p-1">
              <navigation-item
                active-class="bg-purple text-white"
                class="min-h-navigation-item"
                label="För vuxna"
                to="/vuxen"
                :icon="IconAdults"
              ></navigation-item>
            </li>
            <li class="w-1/2 p-1">
              <navigation-item
                active-class="bg-pink-dark text-white"
                class="min-h-navigation-item"
                label="Bli medlem"
                to="/medlem"
                :icon="IconBecomeMember"
              ></navigation-item>
            </li>
            <li class="w-1/2 p-1">
              <div class="flex items-center gap-2 p-2 text-start text-slate-darker">
                <span>Är du vuxen? Besök vår sida för vuxna</span>
                <icon-arrow-right class="h-6 w-6 flex-shrink-0 rotate-180 -scale-x-100" />
              </div>
            </li>
          </ul>
        </nav>
      </div>
    </aside>
  </div>
</template>

<script setup lang="ts">
import IconAboutUs from '@/components/icons/IconAboutUs.vue'
import IconAdults from '@/components/icons/IconAdults.vue'
import IconBecomeMember from '@/components/icons/IconBecomeMember.vue'
import IconChick from '@/components/icons/IconChick.vue'
import IconHome from '@/components/icons/IconHome.vue'
import IconMagazine from '@/components/icons/IconMagazine.vue'
import IconMobilePhone from '@/components/icons/IconMobilePhone.vue'
import IconWhale from '@/components/icons/IconWhale.vue'

const isOpen = ref(false)

const label = computed(() => {
  return isOpen.value ? 'Stäng meny' : 'Öppna meny'
})

watch(isOpen, (value) => {
  hideDocumentScroll(value)
})

const route = useRoute()

watch(
  () => route.path,
  () => {
    isOpen.value = false
  },
)

function hideDocumentScroll(hidden: boolean) {
  const scrollElement = document.scrollingElement || document.documentElement
  scrollElement?.classList.toggle('overflow-hidden', hidden)
}
</script>

<style lang="postcss" scoped>
.mt-navigation {
  margin-top: calc(max(0px, calc(40vh - 267px)));
}

.min-h-navigation-item {
  min-height: calc(40px + 5vh + 3vw);
}

button {
  &:hover {
    background-color: theme('colors.blue.darker') !important;
    opacity: 0.75 !important;
  }
}
</style>
